import * as React from 'react';

// Reusable stroke icon set (paths from the mockup). Size comes from DEFAULT_SIZE per name; pass
// `size` only to override. Usage: <CustomIcon name="copy" />.
// `starFilled` is the only filled glyph.
export type CustomIconName =
  | 'copy'
  | 'mail'
  | 'trash'
  | 'refresh'
  | 'plus'
  | 'star'
  | 'starFilled'
  | 'x'
  | 'chevron'
  | 'chevronDown'
  | 'chevronUp'
  | 'sortAsc'
  | 'sortDesc'
  | 'check'
  | 'info'
  | 'shield'
  | 'tag'
  | 'funnel'
  | 'filterReset'
  | 'wand'
  | 'ban'
  | 'download'
  | 'upload'
  | 'file'
  | 'edit'
  | 'checkCircle'
  | 'xCircle'
  | 'clock';

const PATHS: Record<CustomIconName, string> = {
  copy: '<rect x="9" y="9" width="11" height="11" rx="2"/><path d="M5 15V5a2 2 0 0 1 2-2h10"/>',
  mail: '<rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3.5 6.5 8.5 6 8.5-6"/>',
  trash:
    '<path d="M4 7h16M10 7V5h4v2M6.5 7l.9 12.1A1 1 0 0 0 8.4 20h7.2a1 1 0 0 0 1-.9L17.5 7M10 11v5M14 11v5"/>',
  refresh: '<path d="M20.5 12a8.5 8.5 0 1 1-2.6-6.1"/><path d="M20.5 4.5V10H15"/>',
  plus: '<path d="M12 5.5v13M5.5 12h13"/>',
  star: '<path d="m12 4.5 2.3 4.9 5.2.7-3.8 3.7.9 5.2-4.6-2.5-4.6 2.5.9-5.2L4.5 10l5.2-.7z"/>',
  starFilled: '<path d="m12 4.5 2.3 4.9 5.2.7-3.8 3.7.9 5.2-4.6-2.5-4.6 2.5.9-5.2L4.5 10l5.2-.7z"/>',
  x: '<path d="M6 6l12 12M18 6 6 18"/>',
  chevron: '<path d="m9 6 6 6-6 6"/>',
  chevronDown: '<path d="m6 9 6 6 6-6"/>',
  chevronUp: '<path d="m6 15 6-6 6 6"/>',
  // filled triangles for the active sort direction (idem mockup ▲/▼); paths fill most of the
  // viewBox so the glyph reads large at small render sizes.
  sortAsc: '<path d="M12 4l8 16H4z"/>',
  sortDesc: '<path d="M12 20l-8-16h16z"/>',
  check: '<path d="m5 12.5 4.5 4.5L19 7"/>',
  info: '<circle cx="12" cy="12" r="9"/><path d="M12 11v5M12 7.6v.6"/>',
  shield:
    '<path d="M12 3.5 4.5 7v5.5c0 4.3 3.1 7.6 7.5 8.5 4.4-.9 7.5-4.2 7.5-8.5V7z"/><path d="m9.2 12.3 2 2 3.6-3.8"/>',
  tag:
    '<path d="M12 3v18M16.5 7.2c-.7-1.3-2.4-2-4.5-2-2.5 0-4.2 1.2-4.2 3 0 4.2 9 2.2 9 6.4 0 1.9-1.9 3.2-4.6 3.2-2.3 0-4.1-.8-4.8-2.2"/>',
  funnel: '<path d="M4 5h16l-6.2 7.3V19l-3.6-2v-4.7z"/>',
  filterReset:
    '<path d="M13.758 19.414l-4.758 1.586v-8.5l-4.48 -4.928a2 2 0 0 1 -.52 -1.345v-2.227h16v2.172a2 2 0 0 1 -.586 1.414l-4.414 4.414v1.5"/><path d="M22 22l-5 -5"/><path d="M17 22l5 -5"/>',
  wand:
    '<path d="M5 19 16.5 7.5M14.5 5.5 18.5 9.5M13 3.2l.5 1.3 1.3.5-1.3.5-.5 1.3-.5-1.3L11.2 5l1.3-.5zM19.5 12.2l.4 1.1 1.1.4-1.1.4-.4 1.1-.4-1.1-1.1-.4 1.1-.4z"/>',
  ban: '<circle cx="12" cy="12" r="8.5"/><path d="m6.5 6.5 11 11"/>',
  download: '<path d="M12 4v10M8 10.5l4 4 4-4M5 19h14"/>',
  upload: '<path d="M12 20V10M8 13.5l4-4 4 4M5 5h14"/>',
  file: '<path d="M13 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V9z"/><path d="M13 3v6h6"/>',
  edit: '<path d="M12 20h8"/><path d="M16.5 4.5a2.1 2.1 0 0 1 3 3L8 19l-4 1 1-4z"/>',
  checkCircle: '<circle cx="12" cy="12" r="8.5"/><path d="m8.5 12 2.5 2.5 4.5-5"/>',
  xCircle: '<circle cx="12" cy="12" r="8.5"/><path d="m9.5 9.5 5 5M14.5 9.5l-5 5"/>',
  clock: '<circle cx="12" cy="12" r="8.5"/><path d="M12 7.5V12l3 1.8"/>'
};

// Single source of truth for icon sizes — one entry per icon. Buttons + row lines are 16, the header
// filter is 14, the sort glyphs are 12. Change here → applies everywhere.
const DEFAULT_SIZE: Record<CustomIconName, number> = {
  copy: 18,
  mail: 18,
  trash: 18,
  refresh: 18,
  plus: 18,
  star: 18,
  starFilled: 18,
  x: 18,
  chevron: 18,
  chevronDown: 18,
  chevronUp: 18,
  sortAsc: 12,
  sortDesc: 12,
  check: 18,
  info: 18,
  shield: 18,
  tag: 18,
  funnel: 14,
  filterReset: 16,
  wand: 18,
  ban: 18,
  download: 18,
  upload: 18,
  file: 18,
  edit: 18,
  checkCircle: 18,
  xCircle: 18,
  clock: 18
};

export interface CustomIconProps {
  name: CustomIconName;
  size?: number;
}

const CustomIcon: React.FC<CustomIconProps> = ({ name, size }) => {
  const s = size ?? DEFAULT_SIZE[name];
  const filled = name === 'starFilled' || name === 'sortAsc' || name === 'sortDesc';
  return (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      stroke={filled ? 'none' : 'currentColor'}
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: 'block', flex: 'none' }}
      dangerouslySetInnerHTML={{ __html: PATHS[name] }}
    />
  );
};

export default CustomIcon;
